<template>
  <div class="pending-approvals-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <b-table
        v-if="nominations.length > 0"
        :items="nominationsWithSelection"
        :fields="tableFields"
        hover
        responsive
        borderless
        show-empty
        class="pending-approvals-table__table"
      >
        <template #head(select)>
          <b-form-checkbox :checked="allSelected" :indeterminate="indeterminate" @change="toggleSelectAll" />
        </template>

        <template #cell(select)="{ item }">
          <b-form-checkbox :checked="item.selected" @change="checked => onSelectionChange(item, checked)" />
        </template>

        <template #cell(full_name)="{ item }">
          <span class="pending-approvals-table__name">{{ item.full_name }}</span>
        </template>

        <template #cell(email)="{ item }">
          <span class="pending-approvals-table__email">{{ item.email }}</span>
        </template>

        <template #cell(employee_code)="{ item }">
          <span>{{ item.employee_code }}</span>
        </template>

        <template #cell(program_name)="{ item }">
          <span>{{ item.program_name }}</span>
        </template>

        <template #cell(nominated_by)="{ item }">
          <template v-if="item.nominated_by && item.nominated_by.length > 0">
            <template v-if="item.nominated_by.length === 1">
              <b-badge pill variant="light" class="pending-approvals-table__nominator-badge">
                {{ item.nominated_by[0].full_name }}
              </b-badge>
            </template>
            <template v-else>
              <span
                :id="`nominators-popover-${item.nominated_by[0].recognition_nomination_id}`"
                class="pending-approvals-table__nominators-trigger"
              >
                <b-badge pill variant="light" class="pending-approvals-table__nominator-badge">
                  {{ item.nominated_by.length }} nominators
                </b-badge>
              </span>
              <b-popover
                :target="`nominators-popover-${item.nominated_by[0].recognition_nomination_id}`"
                triggers="hover focus"
                placement="bottom"
              >
                <div
                  v-for="nominator in item.nominated_by"
                  :key="nominator.recognition_nomination_id"
                  class="pending-approvals-table__popover-item"
                >
                  {{ nominator.full_name }}
                </div>
              </b-popover>
            </template>
          </template>
          <template v-else>
            <span class="text-muted">-</span>
          </template>
        </template>
      </b-table>

      <div v-else class="pending-approvals-table__empty">
        <p class="text-muted">No pending approvals.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import AppLoading from '@/components/AppLoading.vue';

const props = defineProps({
  nominations: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showProgramColumn: {
    type: Boolean,
    default: true,
  },
});

const BASE_FIELDS = [
  {
    key: 'select',
    label: '',
    thStyle: { width: '50px' },
  },
  {
    key: 'full_name',
    label: 'Name',
  },
  {
    key: 'email',
    label: 'Email',
  },
  {
    key: 'employee_code',
    label: 'Employee No.',
  },
];

const PROGRAM_FIELD = {
  key: 'program_name',
  label: 'Award',
};

const NOMINATED_BY_FIELD = {
  key: 'nominated_by',
  label: 'Nominated by',
};

const tableFields = computed(() => {
  const fields = [...BASE_FIELDS];
  if (props.showProgramColumn) {
    fields.push(PROGRAM_FIELD);
  }
  fields.push(NOMINATED_BY_FIELD);
  return fields;
});

// Use a reactive Set to track selected nomination IDs (first nomination ID per row)
const selectedIds = ref(new Set());

// Get unique ID for a nomination row (use first nomination's ID)
const getNominationId = nomination => nomination.nominated_by?.[0]?.recognition_nomination_id;

// Computed items with reactive selected state
const nominationsWithSelection = computed(() =>
  props.nominations.map(n => ({
    ...n,
    selected: selectedIds.value.has(getNominationId(n)),
  })),
);

const selectedCount = computed(() => selectedIds.value.size);

const allSelected = computed(() => {
  const total = props.nominations.length;
  return total > 0 && selectedCount.value === total;
});

const indeterminate = computed(() => {
  const total = props.nominations.length;
  return selectedCount.value > 0 && selectedCount.value < total;
});

// Reset selection when nominations change
watch(
  () => props.nominations,
  () => {
    selectedIds.value = new Set();
  },
);

const toggleSelectAll = checked => {
  if (checked) {
    selectedIds.value = new Set(props.nominations.map(getNominationId).filter(Boolean));
  } else {
    selectedIds.value = new Set();
  }
};

const onSelectionChange = (nomination, checked) => {
  const id = getNominationId(nomination);
  if (!id) {
    return;
  }

  const newSet = new Set(selectedIds.value);
  if (checked) {
    newSet.add(id);
  } else {
    newSet.delete(id);
  }
  selectedIds.value = newSet;
};
</script>

<style scoped lang="scss">
.pending-approvals-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__name {
    font-weight: 500;
  }

  &__email {
    color: var(--palette-color-default-lighten-30);
  }

  &__nominator-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
    padding: 0.375rem 0.75rem;
  }

  &__nominators-trigger {
    cursor: pointer;
  }

  &__popover-item {
    padding: 0.25rem 0;
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
